import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { setToken, getToken, removeToken } from "../utils/token";
import {
  hasExceededInactivityLimit,
  startActivityTracking,
  clearActivity,
} from "../utils/sessionActivity";
import { getForcastWeather, parseWeatherData } from "../utils/weatherApi";
import Header from "./Header";
import Main from "./Main";
import Footer from "./Footer";
import "../blocks/app/app.css";
import auth from "../utils/auth";
import api from "../utils/api";
import Profile from "./Profile";
import FavoritesPage from "./FavoritesPage";
import ItemModal from "./ItemModal";
import AddItemModal from "./AddItemModal";
import AddFavoriteModal from "./AddFavoriteModal";
import ConfirmationModal from "./ConfirmationModal";
import RegisterModal from "./RegisterModal";
import LoginModal from "./LoginModal";
import ProtectedRoute from "../utils/ProtectedRoute";
import CurrentUserContext from "../contexts/CurrentUserContext";
import CurrentTemperatureUnitContext from "../contexts/CurrentTemperatureUnitContext";
import EditProfileModal from "./EditProfileModal";
import UpdateToast from "./UpdateToast";
import GalleryPage from "./GalleryPage";
import AddGalleryModal from "./AddGalleryModal";
import GalleryLightbox from "./GalleryLightbox";

function App() {
  const [activeModal, setActiveModal] = useState("");
  const [selectedCard, setSelectedCard] = useState([]);
  const [weather, setWeather] = useState(null);
  const [currentTemperatureUnit, setCurrentTemperatureUnit] = useState("F");
  const [clothingItems, setClothingItems] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showFavoriteConfirmation, setShowFavoriteConfirmation] = useState(false);
  const [favoriteToDelete, setFavoriteToDelete] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [isGalleryUploading, setIsGalleryUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryItemToDelete, setGalleryItemToDelete] = useState(null);
  const [showGalleryConfirmation, setShowGalleryConfirmation] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleOpenRegisterModal = () => setActiveModal("register");
  const handleOpenLoginModal = () => setActiveModal("login");
  const handleOpenEditProfileModal = () => setActiveModal("edit");
  const handleCloseModal = () => {
    setActiveModal("");
  };

  const handleSubmit = (request) => {
    setIsLoading(true);
    request()
      .then(() => {})
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  const handleRegistration = ({ email, password, name, avatar }) => {
    if (!password || !email) return;
    const makeRequest = () => {
      return auth.register(email, password, name, avatar).then(() => {
        handleCloseModal();
        setActiveModal("login");
      });
    };
    handleSubmit(makeRequest);
  };

  const handleLogin = ({ email, password }) => {
    if (!email || !password) {
      return;
    }
    const makeRequest = () => {
      return auth
        .authorize(email, password)
        .then((data) => {
          if (data.token) {
            setToken(data.token);
            return api.getUserInfo(data.token);
          }
        })
        .then((user) => {
          if (!user) {
            return;
          }
          handleCloseModal();
          setCurrentUser(user);
          setIsLoggedIn(true);
          navigate("/profile");
        });
    };
    handleSubmit(makeRequest);
  };

  const handleLogout = () => {
    removeToken();
    clearActivity();
    setCurrentUser(null);
    setIsLoggedIn(false);
    navigate("/");
    setActiveModal("login");
  };

  const handleEditProfileSubmit = (inputValues) => {
    if (!inputValues.name || !inputValues.avatar) {
      return;
    }
    const makeRequest = () => {
      return api.editProfile(inputValues).then((res) => {
        setCurrentUser(res.data);
      });
    };
    handleSubmit(makeRequest);
  };

  const handleCreateModal = () => {
    setActiveModal("create");
  };

  const handleSelectedCard = (card) => {
    setActiveModal("preview");
    setSelectedCard(card);
  };

  const openConfirmationModal = (card) => {
    setCardToDelete(card);
    setShowConfirmationModal(true);
  };

  const handleDeleteItem = () => {
    if (!cardToDelete) return;
    const makeRequest = () => {
      return api.deleteItem(cardToDelete._id).then(() => {
        const newClothingItem = clothingItems.filter((card) => {
          return card._id !== cardToDelete._id;
        });
        setClothingItems(newClothingItem);
        setShowConfirmationModal(false);
        setCardToDelete(null);
        handleCloseModal();
      });
    };
    handleSubmit(makeRequest);
  };

  const handleAddItemSubmit = ({ name, weather, imageUrl }) => {
    const makeRequest = () => {
      return api.addItem({ name, weather, imageUrl }).then((res) => {
        setClothingItems((prevItems) => [res.data, ...prevItems]);
        handleCloseModal();
      });
    };
    handleSubmit(makeRequest);
  };

  const handleCreateFavoriteModal = () => {
    setActiveModal("createFavorite");
  };

  const handleAddFavoriteSubmit = ({ type, url, title }) => {
    if (!url) return;
    const makeRequest = () => {
      return api.addFavorite({ type, url, title }).then((res) => {
        setFavorites((prevFavorites) => [res.data, ...prevFavorites]);
        handleCloseModal();
      });
    };
    handleSubmit(makeRequest);
  };

  const openFavoriteConfirmation = (favorite) => {
    setFavoriteToDelete(favorite);
    setShowFavoriteConfirmation(true);
  };

  const handleDeleteFavorite = () => {
    if (!favoriteToDelete) return;
    api
      .deleteFavorite(favoriteToDelete._id)
      .then(() => {
        setFavorites((prevFavorites) =>
          prevFavorites.filter((favorite) => favorite._id !== favoriteToDelete._id)
        );
      })
      .catch(console.error)
      .finally(() => {
        setShowFavoriteConfirmation(false);
        setFavoriteToDelete(null);
      });
  };

  const handleCreateGalleryModal = () => {
    setActiveModal("createGallery");
  };

  const handleAddGalleryItems = (files, title) => {
    if (!files || files.length === 0) return;
    setIsGalleryUploading(true);
    Promise.all(Array.from(files).map((file) => api.uploadGalleryFile(file, title)))
      .then(() => {
        refreshGalleryItems();
        handleCloseModal();
      })
      .catch((err) => {
        console.error("Failed to upload file(s):", err);
      })
      .finally(() => setIsGalleryUploading(false));
  };

  const handleAddGalleryLink = ({ kind, url, title }) => {
    if (!url) return;
    setIsGalleryUploading(true);
    api
      .addGalleryLink({ kind, url, title })
      .then(() => {
        refreshGalleryItems();
        handleCloseModal();
      })
      .catch((err) => {
        console.error("Failed to save link:", err);
      })
      .finally(() => setIsGalleryUploading(false));
  };

  const openGalleryConfirmation = (item) => {
    setGalleryItemToDelete(item);
    setShowGalleryConfirmation(true);
  };

  const handleDeleteGalleryItem = () => {
    if (!galleryItemToDelete) return;
    api
      .deleteGalleryItem(galleryItemToDelete._id)
      .then(() => {
        setGalleryItems((prevItems) =>
          prevItems.filter((galleryItem) => galleryItem._id !== galleryItemToDelete._id)
        );
      })
      .catch((err) => {
        console.error("Failed to delete gallery item:", err);
      })
      .finally(() => {
        setLightboxIndex(null);
        setShowGalleryConfirmation(false);
        setGalleryItemToDelete(null);
      });
  };

  const handleToggleSwitchChange = () => {
    setCurrentTemperatureUnit(currentTemperatureUnit === "F" ? "C" : "F");
  };
  const handleCardLike = (item) => {
    const token = getToken();
    const { _id } = item;

    if (!currentUser) {
      console.log("CurrentUser is not available");
      return;
    }

    const isLiked = item.likes.some((id) => id === currentUser._id);

    const likeRequest = isLiked
      ? api.removeCardLike(_id, token)
      : api.addCardLike(_id, token);

    likeRequest
      .then((updatedCard) => {
        setClothingItems((cards) =>
          cards.map((card) => (card._id === item._id ? updatedCard.data : card))
        );
      })
      .catch(console.error);
  };

  // useEffect to handleCloseModal;

  useEffect(() => {
    if (!activeModal) return;

    const handleEscClose = (e) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };

    document.addEventListener("keydown", handleEscClose);

    return () => {
      document.removeEventListener("keydown", handleEscClose);
    };
  }, [activeModal]);

  // useEffect to fetch weather data

  useEffect(() => {
    getForcastWeather()
      .then((data) => {
        const parsedWeather = parseWeatherData(data);
        setWeather(parsedWeather);
      })
      .catch((err) => {
        console.error(`Failed to fetch weather data: ${err}`);
      });
  }, []);

  // useEffect to fetch clothing items

  useEffect(() => {
    api
      .getItems()
      .then((items) => {
        setClothingItems(items);
      })
      .catch((error) => {
        console.error("Failed to fetch items:", error);
        setClothingItems([]);
      });
  }, [isLoggedIn]);

  // useEffect to fetch favorites (requires auth, so only when logged in)

  useEffect(() => {
    if (!isLoggedIn) {
      setFavorites([]);
      return;
    }
    api
      .getFavorites()
      .then((items) => {
        setFavorites(items);
      })
      .catch((error) => {
        console.error("Failed to fetch favorites:", error);
        setFavorites([]);
      });
  }, [isLoggedIn]);

  // useEffect to load this user's gallery items from the server

  const refreshGalleryItems = () => {
    api
      .getGalleryItems()
      .then((items) => setGalleryItems(items))
      .catch(console.error);
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setGalleryItems([]);
      return;
    }
    refreshGalleryItems();
  }, [isLoggedIn, currentUser?._id]);

  // useEffect to check the token and get user info

  useEffect(() => {
    const jwt = getToken();

    if (!jwt) {
      return;
    }

    if (hasExceededInactivityLimit()) {
      removeToken();
      clearActivity();
      return;
    }

    api
      .getUserInfo(jwt)
      .then((user) => {
        setIsLoggedIn(true);
        setCurrentUser(user);
      })
      .catch(console.error);
  }, []);

  // useEffect to track activity while logged in, and log out if the
  // person has been away past the inactivity limit while the tab stayed open

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const stopTracking = startActivityTracking();

    const inactivityCheck = setInterval(() => {
      if (hasExceededInactivityLimit()) {
        handleLogout();
      }
    }, 5 * 60 * 1000); // check every 5 minutes

    return () => {
      stopTracking();
      clearInterval(inactivityCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <CurrentUserContext.Provider value={{ currentUser, isLoggedIn }}>
      <CurrentTemperatureUnitContext.Provider
        value={{ currentTemperatureUnit, handleToggleSwitchChange }}
      >
        <div className="page__section">
          <UpdateToast />
          <Header
            userName={currentUser?.name}
            userAvatar={currentUser?.avatar}
            isAuthorized={isLoggedIn}
            onCreateModal={handleCreateModal}
            handleProfileClick={() => navigate("/profile")}
            onRegisterModal={handleOpenRegisterModal}
            onLoginModal={handleOpenLoginModal}
          />

          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Main
                    weather={weather}
                    onSelectCard={handleSelectedCard}
                    clothingItems={clothingItems}
                    onCardLike={handleCardLike}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile
                    userName={currentUser?.name}
                    userAvatar={currentUser?.avatar}
                    clothingItems={clothingItems}
                    onCreateModal={handleCreateModal}
                    onSelectCard={handleSelectedCard}
                    onCardLike={handleCardLike}
                    onEditProfile={handleOpenEditProfileModal}
                    onLogout={handleLogout}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <FavoritesPage
                    userName={currentUser?.name}
                    userAvatar={currentUser?.avatar}
                    favorites={favorites}
                    onCreateFavorite={handleCreateFavoriteModal}
                    onDeleteFavorite={openFavoriteConfirmation}
                    onEditProfile={handleOpenEditProfileModal}
                    onLogout={handleLogout}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/gallery"
              element={
                <ProtectedRoute>
                  <GalleryPage
                    userName={currentUser?.name}
                    userAvatar={currentUser?.avatar}
                    userId={currentUser?._id}
                    galleryItems={galleryItems}
                    onCreateGallery={handleCreateGalleryModal}
                    onDeleteGalleryItem={openGalleryConfirmation}
                    onSelectGalleryItem={setLightboxIndex}
                    onEditProfile={handleOpenEditProfileModal}
                    onLogout={handleLogout}
                    onMigrated={refreshGalleryItems}
                  />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <Footer />
          {activeModal === "create" && (
            <AddItemModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "create"}
              onAddItem={handleAddItemSubmit}
              isLoading={isLoading}
            />
          )}
          {activeModal === "createFavorite" && (
            <AddFavoriteModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "createFavorite"}
              onAddFavorite={handleAddFavoriteSubmit}
              isLoading={isLoading}
            />
          )}
          {activeModal === "createGallery" && (
            <AddGalleryModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "createGallery"}
              onAddGalleryItems={handleAddGalleryItems}
              onAddGalleryLink={handleAddGalleryLink}
              isLoading={isGalleryUploading}
            />
          )}
          {activeModal === "edit" && (
            <EditProfileModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "edit"}
              handleEditProfile={handleEditProfileSubmit}
              isLoading={isLoading}
            />
          )}
          {activeModal === "register" && (
            <RegisterModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "register"}
              handleRegistration={handleRegistration}
              handleOpenLoginModal={handleOpenLoginModal}
              isLoading={isLoading}
            />
          )}
          {activeModal === "login" && (
            <LoginModal
              handleCloseModal={handleCloseModal}
              isOpen={activeModal === "login"}
              handleLogin={handleLogin}
              handleOpenRegisterModal={handleOpenRegisterModal}
              isLoading={isLoading}
            />
          )}
          {activeModal === "preview" && (
            <ItemModal
              selectedCard={selectedCard}
              onClose={handleCloseModal}
              onDelete={openConfirmationModal}
              openConfirmationModal={openConfirmationModal}
              isLoading={isLoading}
            />
          )}
          {showConfirmationModal && (
            <ConfirmationModal
              confirmation="item-delete"
              onConfirmDelete={handleDeleteItem}
              onClose={() => {
                setShowConfirmationModal(false);
              }}
              onCancel={() => setShowConfirmationModal(false)}
            />
          )}
          {showFavoriteConfirmation && (
            <ConfirmationModal
              confirmation="favorite-delete"
              onConfirmDelete={handleDeleteFavorite}
              onClose={() => setShowFavoriteConfirmation(false)}
              onCancel={() => setShowFavoriteConfirmation(false)}
            />
          )}
          {lightboxIndex !== null && galleryItems[lightboxIndex] && (
            <GalleryLightbox
              items={galleryItems}
              activeIndex={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
              onNavigate={setLightboxIndex}
              onDelete={openGalleryConfirmation}
            />
          )}
          {showGalleryConfirmation && (
            <ConfirmationModal
              confirmation="gallery-delete"
              onConfirmDelete={handleDeleteGalleryItem}
              onClose={() => setShowGalleryConfirmation(false)}
              onCancel={() => setShowGalleryConfirmation(false)}
            />
          )}
        </div>
      </CurrentTemperatureUnitContext.Provider>
    </CurrentUserContext.Provider>
  );
}

export default App;
